<template>
  <div>
    <withdrawal-request-sidebar />
    <save-financial-adjustment-sidebar />
    <div style="position: relative">
      <app-breadcrumb :breadcrumbTitle="false">
        <b-button
          v-if="canCreate"
          variant="primary"
          @click="openNewFinancialAdjustmentSidebar({ saveAction })"
          style="position: absolute; right: 13"
        >
          <feather-icon icon="PlusCircleIcon" class="mr-50" />
          <span class="text-nowrap">Novo ajuste</span>
        </b-button>
      </app-breadcrumb>
    </div>

    <!--
      Os quatro números que o Financeiro usa para decidir.

      Antes eram Crédito, Débito e Quantidade total: descreviam o recorte sem dizer o que fazer
      com ele. Agora a primeira leitura é a fila de trabalho, e as duas últimas quebram o recorte
      pelos dois CNPJs — com o mesmo rótulo e a mesma cor da coluna "Empresa pagadora" da tabela,
      para o olho ligar card e linha sem legenda.

      O gráfico de rosca crédito × débito saiu: dois valores não precisam de gráfico, e o espaço
      virou o quarto card.
    -->
    <b-row class="match-height">
      <b-col md="3" sm="6">
        <b-card no-body class="card-statistics">
          <b-card-body>
            <span class="totalizer-label">Aguardando aprovação</span>
            <h4
              class="font-weight-bolder mb-0 text-warning"
              data-testid="adjustments-totalizer-pending-amount"
            >
              {{ pendingAmount | toCurrency }}
            </h4>
            <small class="text-muted">
              {{ pendingCount }} {{ pendingCount === 1 ? "lançamento" : "lançamentos" }}
              <template v-if="oldestPendingLabel">· {{ oldestPendingLabel }}</template>
            </small>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col md="3" sm="6">
        <b-card no-body class="card-statistics">
          <b-card-body>
            <span class="totalizer-label">Efeito líquido no recorte</span>
            <h4
              class="font-weight-bolder mb-0"
              data-testid="adjustments-totalizer-net-amount"
            >
              {{ netAmount | toCurrency }}
            </h4>
            <small class="text-muted">
              <span class="text-success font-weight-bold">
                Crédito {{ creditAmount | toCurrency }}
              </span>
              ·
              <span class="text-danger font-weight-bold">
                Débito {{ debtAmount | toCurrency }}
              </span>
            </small>
          </b-card-body>
        </b-card>
      </b-col>

      <!--
        Um card por empresa pagadora cadastrada, e não dois fixos: `paying_companies` é tabela, e
        uma terceira empresa apareceria no backend sem aparecer aqui. Quem não tem CNPJ classificado
        cai no card residual abaixo — some-lo faria os cards não fecharem com o total.
      -->
      <b-col
        v-for="company in payingCompanyCards"
        :key="company.id"
        md="3"
        sm="6"
      >
        <b-card no-body class="card-statistics">
          <b-card-body>
            <b-badge :variant="company.variant" class="mb-25">
              {{ company.name }}
            </b-badge>
            <h4
              class="font-weight-bolder mb-0"
              :data-testid="`adjustments-totalizer-company-${company.id}-amount`"
            >
              {{ company.amount | toCurrency }}
            </h4>
            <small class="text-muted">
              {{ company.total }} {{ company.total === 1 ? "lançamento" : "lançamentos" }}
              <template v-if="company.cnpj">· {{ company.cnpj }}</template>
            </small>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BBadge,
  BButton,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import AppBreadcrumb from "@core/layouts/components/AppBreadcrumb.vue";
import WithdrawalRequestSidebar from "@/modules/withdrawal/components/WithdrawalRequestSidebar";
import SaveFinancialAdjustmentSidebar from "@/modules/financial_adjustment/components/SaveFinancialAdjustmentSidebar";
import * as types from "../store/types";
import * as creditTypes from "@/constants/credit_types";
import { CREATE_ACTION, FINANCIAL_ADJUSTMENTS } from "@/constants/resources";
import {
  HUB_ID,
  PLATFORM_ID,
  PAYING_COMPANIES,
  NONE_FILTER,
} from "@/constants/paying_companies";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BBadge,
    BButton,
    AppBreadcrumb,
    WithdrawalRequestSidebar,
    SaveFinancialAdjustmentSidebar,
  },
  props: {
    /**
     * A resposta do endpoint de totalizadores:
     * `{ by_credit_type: [], pending: {}, by_paying_company: [] }`.
     *
     * ⚠️ Era um Array antes desta entrega, e o default abaixo é o que impede a tela de quebrar
     * durante o primeiro carregamento, quando ainda não há resposta nenhuma.
     */
    totalizer: {
      type: Object,
      default: () => ({}),
    },
    saveAction: Function,
  },
  computed: {
    canCreate() {
      return this.$can(CREATE_ACTION, FINANCIAL_ADJUSTMENTS);
    },
    byCreditType() {
      return this.totalizer.by_credit_type || [];
    },
    creditAmount() {
      return this.amountForCreditType(creditTypes.CREDIT);
    },
    debtAmount() {
      return this.amountForCreditType(creditTypes.DEBT);
    },
    // Débito ENTRA COM SINAL NEGATIVO aqui: no banco `value` é sempre positivo e quem dá o sinal
    // é `credit_type`. Somar os dois sem inverter o débito devolveria o volume movimentado, não
    // o efeito — e é o efeito que decide se o mês tira ou põe dinheiro.
    netAmount() {
      return this.creditAmount - this.debtAmount;
    },
    pendingCount() {
      return Number((this.totalizer.pending || {}).total) || 0;
    },
    pendingAmount() {
      return Number((this.totalizer.pending || {}).amount) || 0;
    },
    oldestPendingLabel() {
      const oldest = (this.totalizer.pending || {}).oldest_launch_date;
      if (!oldest) return "";
      const days = Math.floor(
        (Date.now() - new Date(oldest).getTime()) / (1000 * 60 * 60 * 24),
      );
      if (days < 1) return "o mais antigo é de hoje";
      return `o mais antigo há ${days} ${days === 1 ? "dia" : "dias"}`;
    },
    payingCompanyCards() {
      return (this.totalizer.by_paying_company || []).map((row) => {
        const id = row.paying_company_id;
        const known = PAYING_COMPANIES.find((company) => company.id === Number(id));
        return {
          id: id || NONE_FILTER,
          name: known ? known.name : row.paying_company_name,
          cnpj: known ? known.cnpj : "",
          variant: this.companyVariant(id),
          total: Number(row.total) || 0,
          amount: Number(row.amount) || 0,
        };
      });
    },
  },
  methods: {
    ...mapActions({
      openNewFinancialAdjustmentSidebar: types.OPEN_SAVE_FINANCIAL_ADJUSTMENT_SIDEBAR,
    }),
    amountForCreditType(type) {
      const row = this.byCreditType.find((item) => item.credit_type === type);
      return row ? Number(row.amount) || 0 : 0;
    },
    // Mesmas cores dos badges da tabela e do detalhe. success/danger ficam de fora de propósito:
    // nesta tela eles já significam crédito e débito.
    companyVariant(id) {
      if (Number(id) === HUB_ID) return "light-info";
      if (Number(id) === PLATFORM_ID) return "light-primary";
      return "light-secondary";
    },
  },
};
</script>

<style lang="scss">
.totalizer-label {
  display: block;
  font-size: 0.9rem;
  color: #6e6b7b;
  margin-bottom: 0.25rem;
}
</style>
